import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articlesBySlug } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

const siteUrl = import.meta.env.VITE_SITE_URL;

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!slug || !articlesBySlug[slug]) {
    return <NotFound />;
  }

  const article = articlesBySlug[slug];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.subtitle,
    url: `${siteUrl}/article/${slug}`,
    datePublished: '2026-06-25',
    dateModified: '2026-06-25',
    author: {
      '@type': 'Organization',
      name: 'ACBuySpreadsheet',
      url: `${siteUrl}/`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'ACBuySpreadsheet',
      url: `${siteUrl}/`,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/acdcfd47829ab306d380ea84eb14e50b.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${slug}`,
    },
  };

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />

      <article className="pt-24 pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex flex-col lg:flex-row gap-10 lg:gap-16">
          <div className="lg:w-[60%]">
            <span className="inline-flex items-center gap-2 text-xs font-semibold text-accent-700 uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full bg-accent-500"></span>
              {article.category}
            </span>

            <h1 className="mt-4 text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>

            <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed">
              {article.subtitle}
            </p>

            <div className="mt-8 prose prose-lg max-w-none text-foreground-700 leading-relaxed space-y-6">
              <p className="text-sm md:text-base">{article.intro}</p>

              {article.sections.map((section, si) => (
                <div key={si}>
                  {section.heading && (
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
                      {section.heading}
                    </h2>
                  )}
                  {section.subheading && (
                    <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-800 mt-8 mb-3">
                      {section.subheading}
                    </h3>
                  )}
                  {section.paragraphs.map((p, pi) => (
                    <p key={pi} className="text-sm md:text-base">{p}</p>
                  ))}
                </div>
              ))}

              {article.comparisonTable && (
                <div className="overflow-x-auto my-8">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-background-300">
                        {article.comparisonTable.headers.map((h, hi) => (
                          <th key={hi} className="text-left py-3 pr-4 font-semibold text-foreground-900 whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {article.comparisonTable.rows.map((row, ri) => (
                        <tr key={ri} className="border-b border-background-200">
                          {row.cells.map((cell, ci) => (
                            <td
                              key={ci}
                              className={`py-3 pr-4 whitespace-nowrap ${
                                ci === 1 ? 'font-medium text-accent-700' : 'text-foreground-600'
                              }`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-8 p-5 bg-background-100 rounded-lg">
                <p className="text-sm font-semibold text-foreground-800 mb-2">
                  Explore all 14 product categories:
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Shoes · Hoodies/Sweaters · T-Shirts · Jackets · Pants/Shorts · Headwear · Sets · Underwear/Underpants · Jersey · Accessories · Bags · Belts · Perfumes · Other Stuff
                </p>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-700 hover:text-foreground-950 transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                Back to ACBuySpreadsheet Home
              </Link>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-sm font-semibold text-accent-700 hover:text-accent-800 transition-colors"
              >
                Visit Main Store
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </a>
            </div>
          </div>

          <div className="lg:w-[40%]">
            <div className="sticky top-24">
              <div className="w-full aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={article.sidebarImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-6 p-5 bg-background-100 rounded-lg">
                <h4 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider">Quick Links</h4>
                <div className="mt-3 space-y-2">
                  <Link to="/" className="block text-sm text-foreground-600 hover:text-accent-700 transition-colors">
                    ← ACBuySpreadsheet Home
                  </Link>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="block text-sm text-foreground-600 hover:text-accent-700 transition-colors"
                  >
                    Visit Main Store →
                  </a>
                  <Link
                    to="/article/what-is-acbuyspreadsheet"
                    className="block text-sm text-foreground-600 hover:text-accent-700 transition-colors"
                  >
                    Beginner Guide
                  </Link>
                  <Link
                    to="/article/acbuyspreadsheet-vs-google-sheets"
                    className="block text-sm text-foreground-600 hover:text-accent-700 transition-colors"
                  >
                    Comparison Guide
                  </Link>
                  <Link
                    to="/article/acbuyspreadsheet-category-guide"
                    className="block text-sm text-foreground-600 hover:text-accent-700 transition-colors"
                  >
                    Category Strategy
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}